import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { Instagram, Facebook, Twitter, Youtube, MapPin, Phone, Mail, ChevronDown } from "lucide-react";

const COLS = [
  {
    title: "Explore",
    links: [
      { to: "/products", label: "All E-Cycles" },
      { to: "/products?cat=urban", label: "Urban" },
      { to: "/products?cat=foldable", label: "Foldable" },
      { to: "/products?cat=cargo", label: "Cargo" },
    ],
  },
  {
    title: "Programs",
    links: [
      { to: "/rent", label: "Rent an E-Cycle" },
      { to: "/rent#gig", label: "For Gig Workers" },
      { to: "/service", label: "Service & Repairs" },
      { to: "/about#franchise", label: "Open a Store" },
    ],
  },
  {
    title: "Support",
    links: [
      { to: "/stores", label: "Find a Store" },
      { to: "/contact", label: "Contact Us" },
      { to: "/contact#faq", label: "FAQs" },
      { to: "/warranty", label: "Warranty" },
    ],
  },
  {
    title: "Company",
    links: [
      { to: "/about", label: "Our Story" },
      { to: "/about#mission", label: "Mission" },
      { to: "/about#franchise", label: "Partner With Us" },
      { to: "/contact", label: "Press" },
    ],
  },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#0B1320] text-white"
    >
      <div className="container-x py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_repeat(4,1fr)] gap-x-10 gap-y-6 lg:gap-y-8">
          <div>
            <a href="/" data-testid="footer-brand-logo" aria-label="eBikeGo Store home" className="inline-flex items-center gap-2.5">
              <img
                src="/ebikego-icon.png"
                alt=""
                className="h-9 w-auto"
              />
              <span className="font-outfit font-bold text-white tracking-tight text-2xl leading-none">
                eBikeGo
              </span>
              <span className="font-outfit font-normal text-slate-400 tracking-tight text-2xl leading-none -ml-1 relative top-[3px]">
                Store
              </span>
            </a>
            <p className="mt-6 text-sm text-slate-300 leading-relaxed max-w-xs">
              Electric bicycles for work, commute and everyday earning. Rental,
              retail and service — under one roof, built for India.
            </p>
            <div className="mt-6 space-y-3 text-sm text-slate-300">
              <div className="flex items-start gap-2">
                <MapPin size={16} className="mt-0.5 text-[#3FE0A6] shrink-0" />
                <span>
                  Pramukh Plaza, Cardinal Gracious Rd, Andheri East, Mumbai 400099
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Phone size={16} className="text-[#3FE0A6]" />
                <a href="tel:+918976773152" className="hover:text-white">
                  +91 89767 73152
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Mail size={16} className="text-[#3FE0A6]" />
                <a href="mailto:info@ebikego.in" className="hover:text-white">
                  info@ebikego.in
                </a>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              {[
                { Icon: Instagram, url: "https://www.instagram.com/ebikego/?hl=en" },
                { Icon: Facebook, url: "https://facebook.com/ebikego" },
                { Icon: Twitter, url: "https://twitter.com/ebikego" },
                { Icon: Youtube, url: "https://youtube.com/@ebikego" },
              ].map(({ Icon, url }, i) => (
                <a
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 inline-flex items-center justify-center border border-white/20 text-white/80 hover:text-[#3FE0A6] hover:border-[#3FE0A6] transition rounded-full"
                  aria-label="social"
                  data-testid={`footer-social-${i}`}
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>

            {/* An EBG Group Company badge — inside brand column so the layout stays balanced */}
            <a
              href="https://theebg.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ebg-group-badge"
              className="mt-8 inline-flex items-center gap-4 rounded-2xl border border-white/12 bg-black/40 px-5 py-3.5 hover:border-[#C8A24B]/50 transition-colors"
            >
              <img src="/ebg-logo.png" alt="EBG" className="h-11 w-auto" />
              <span className="h-11 w-px bg-white/15" />
              <span className="leading-tight">
                <span className="block font-outfit text-base text-white">
                  An <span className="text-[#C8A24B]">EBG</span> Group
                </span>
                <span className="block font-outfit text-base text-white">Company</span>
                <span className="block text-xs text-slate-400 mt-0.5">theebg.com ↗</span>
              </span>
            </a>
          </div>

          {COLS.map((c) => (
            <FooterColumn key={c.title} col={c} />
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-slate-400">
          <p>
            © {new Date().getFullYear()} eBikeGo Pvt. Ltd. All rights reserved.
            <span className="ml-2 text-[#3FE0A6]">Proudly made in India.</span>
          </p>
          <p>
            Design &amp; Development by{" "}
            <a
              href="https://www.reachvel.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-credit"
              className="text-white hover:text-[#3FE0A6] font-semibold transition-colors"
            >
              Reachvel
            </a>
          </p>
          <div className="flex flex-wrap gap-6">
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/warranty" className="hover:text-white">Warranty</Link>
            <Link to="/shipping" className="hover:text-white">Shipping</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* Footer link group — collapsible accordion on mobile, always open on desktop */
function FooterColumn({ col }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10 lg:border-none" data-testid={`footer-col-${col.title.toLowerCase()}`}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        aria-expanded={open}
        className="w-full flex items-center justify-between py-4 lg:py-0 lg:mb-5 lg:cursor-default"
        data-testid={`footer-col-toggle-${col.title.toLowerCase()}`}
      >
        <span className="font-outfit font-bold text-sm text-white tracking-wide uppercase">
          {col.title}
        </span>
        <ChevronDown
          size={18}
          className={`text-slate-400 lg:hidden transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      <ul className={`space-y-3 overflow-hidden transition-all lg:!max-h-none lg:opacity-100 lg:pb-0 ${open ? "max-h-96 opacity-100 pb-4" : "max-h-0 opacity-0 lg:opacity-100"}`}>
        {col.links.map((l) => (
          <li key={l.label}>
            <Link
              to={l.to}
              className="text-sm text-slate-300 hover:text-[#3FE0A6] transition-colors"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
